import React from "react";
import {
  ArrowRightLeft,
  FileInput,
  Cog,
  FileOutput,
  CheckCircle2,
  ShieldCheck,
  Eye,
  Brain,
  Workflow,
  Sparkles,
  X,
  ScanLine,
  Braces,
  Landmark,
  Scale,
  FileCode,
  Code2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";
import { FineTuningCallout } from "@/components/FineTuningCallout";

const steps = [
  { icon: FileInput, title: "Paper remittance in", desc: "Faxed, scanned, and mailed EOBs and remittance advices land in Hewto.ai — no format prep required." },
  { icon: Cog, title: "EOB capture & validate", desc: "Vision-language models extract every payment, adjustment, and reason code, with per-field confidence scoring." },
  { icon: ArrowRightLeft, title: "Paper to 835 conversion", desc: "Captured remittance data is converted into a compliant ANSI X12 835 Electronic Remittance Advice." },
  { icon: FileOutput, title: "835 out", desc: "Balanced, posting-ready 835 files are delivered to your practice management or RCM system via SFTP, API, or webhook." },
];

const covered = [
  "Payer, payee, and check / EFT payment details",
  "Claim-level paid amounts, patient responsibility, and status",
  "Service-line payments, units, and procedure codes",
  "CAS adjustment groups with CARC / RARC reason codes",
  "Provider-level adjustments and interest (PLB)",
  "Multi-page and multi-claim remittances, stitched and balanced",
];

const crosswalk = [
  "Payer & payee details → NM1*PR / NM1*PE (Loops 1000A / 1000B)",
  "Payment amount & method → BPR segment (check or ACH/EFT)",
  "Check / EFT trace number → TRN segment",
  "Claim totals & status → CLP segment (Loop 2100)",
  "Service-line payments → SVC + DTM segments",
  "Adjustments & reason codes (CO / PR / OA) → CAS segments",
  "Provider balancing adjustments → PLB segment",
];

const benefits = [
  "Post paper remittances automatically — no manual EOB keying",
  "Balanced output: BPR totals, CLP sums, and service lines always reconcile",
  "CAS adjustments preserved with CARC / RARC codes for denial workflows",
  "Handles any payer EOB layout — no templates to build or maintain",
  "Full audit trail from paper EOB image to 835 transaction",
  "SFTP, API, or webhook delivery into your PM / RCM system",
];

const capabilities = [
  { icon: ScanLine, title: "EOB OCR at any quality", desc: "Reads scanned, faxed, and photographed remittance documents — dense tables, multi-page EOBs, and stamped or annotated copies." },
  { icon: Scale, title: "Payment balancing built in", desc: "Every 835 is validated so claim payments sum to service lines and the BPR payment matches — catching extraction errors before posting." },
  { icon: Landmark, title: "ERA 835 generation service", desc: "Full-service paper remittance to 835 conversion with human-in-the-loop QA and SLAs — an extension of your payment posting team." },
  { icon: Code2, title: "835 conversion API", desc: "POST a paper EOB image or PDF and receive a compliant X12 835 in response. Webhooks, sandbox credentials, and predictable schemas." },
];

const faqs = [
  {
    q: "What is paper to 835 conversion?",
    a: "Paper to 835 conversion turns paper remittance documents — EOBs (Explanations of Benefits), EOPs, and paper remittance advices — into compliant ANSI X12 835 Electronic Remittance Advice files. Hewto.ai automates the entire workflow: EOB capture, payment data validation, balancing, and 835 EDI generation.",
  },
  {
    q: "What is EOB automation?",
    a: "EOB automation is the use of AI to read Explanation of Benefits documents and turn them into posting-ready data — or full 835 files — without manual keying. Hewto.ai's EOB automation covers the entire flow: capture from fax or mailroom, extraction of payments and adjustments, balancing, and 835 generation, with human review only for low-confidence fields.",
  },
  {
    q: "How does Hewto.ai convert a paper EOB to an 835?",
    a: "Hewto.ai's EOB conversion pipeline ingests the paper remittance from fax, scan, digital mailroom, or SFTP, uses vision-language models fine-tuned on EOB layouts to capture every payment, adjustment, and reason code, validates and balances the data, and maps it to a compliant 835 transaction — delivered to your practice management or RCM system with no manual keying.",
  },
  {
    q: "Which paper documents can be converted to 835?",
    a: "Any payer remittance document: EOBs, EOPs (Explanations of Payment), paper remittance advices, and Medicare SPRs — from any payer, in any layout. Because Hewto.ai's models are vision-based rather than template-based, layout variations across payers are handled automatically, and the models can be fine-tuned on your specific EOB formats.",
  },
  {
    q: "How are adjustments and reason codes handled in the 835?",
    a: "Every adjustment on the paper EOB is captured with its group code (CO, PR, OA, PI, CR) and CARC / RARC reason codes, then written to CAS segments at the claim or service-line level exactly as the payer adjudicated it — preserving the data denial-management and posting workflows depend on.",
  },
  {
    q: "How does Hewto.ai make sure the 835 balances?",
    a: "Every generated 835 is validated for financial balance: service-line payments (SVC) plus adjustments (CAS) equal the claim charge, claim payments (CLP) sum to the total payment (BPR), and provider-level adjustments (PLB) reconcile the remainder. Files that don't balance are flagged for review instead of posting bad data.",
  },
  {
    q: "Who uses paper to 835 conversion?",
    a: "Providers, billing companies, RCM vendors, and payment posting teams buried in paper EOBs — especially where payers still mail paper remittances. Converting paper to 835 lets those payments auto-post in the practice management system instead of being keyed by hand.",
  },
  {
    q: "Can Hewto.ai handle multi-page and multi-claim EOBs?",
    a: "Yes. Multi-page remittances are stitched together, claims are split into separate CLP loops, and check-level totals are reconciled across every page — a common failure point for template-based EOB OCR that Hewto.ai handles natively.",
  },
  {
    q: "What accuracy can we expect on EOB data extraction?",
    a: "Hewto.ai achieves 99%+ field-level extraction accuracy on remittance documents, with per-field confidence scores and optional human-in-the-loop review so low-confidence payments and adjustments are verified before they post.",
  },
  {
    q: "Is Hewto.ai HIPAA compliant for remittance data?",
    a: "Yes. Remittance documents contain PHI and are processed in HIPAA-aligned environments with encryption in transit and at rest. BAAs are available for covered entities and business associates, with hosted API, private cloud, and on-premise deployment options.",
  },
  {
    q: "How can we evaluate Hewto.ai on our own paper remittances?",
    a: "Send us a sample batch of your paper EOBs — including your densest multi-page remittances — and we will convert them to 835 and share a field-level accuracy and balancing report so you can compare Hewto.ai against your current keying or OCR process before signing anything.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const PaperTo835 = () => (
  <PageTransition>
    <Seo
      title="EOB Automation & Paper to 835 Conversion — EOB Processing | Hewto.ai"
      description="Hewto.ai automates EOB processing and paper to 835 conversion — turning faxed and scanned EOBs, EOPs, and paper remittance advices into compliant ANSI X12 835 Electronic Remittance Advice files. EOB automation with CAS reason-code capture, balanced payments, and posting-ready 835 output for providers, billing companies, and RCM teams."
      keywords="eob automation, eob processing, paper to 835, paper to 835 conversion, eob to 835, eob conversion, eob to era, paper remittance to 835, 835 conversion, 835 era generation, era 835 file, remittance advice automation, remittance advice ocr, eob ocr, eob data extraction, explanation of benefits ocr, paper eob conversion, 835 remittance file, x12 835 generation, payment posting automation, eob to edi, paper remittance conversion, cas code extraction, carc rarc extraction, denial management automation, rcm automation, 835 file generator, convert eob to 835 era"
      canonical="https://www.hewto.ai/paper-to-835"
      jsonLd={faqJsonLd}
    />
    <PageHero
      eyebrow="Products · Paper to 835"
      title="Paper EOB to 835 ERA conversion, automated"
      subtitle="Hewto.ai automates paper to 835 conversion and EOB conversion — turning faxed, scanned, and mailed EOBs and remittance advices into compliant ANSI X12 835 Electronic Remittance Advice files. Balanced, posting-ready remittances for providers, billing companies, and RCM teams — no manual EOB keying."
    />

    {/* PIPELINE */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            The pipeline
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Paper remittance to 835 in four steps
          </h2>
          <p className="mt-4 leading-relaxed text-slate-600">
            Hewto.ai handles the full paper to 835 conversion pipeline — from EOB intake to
            balanced, posting-ready 835 output.
          </p>
        </div>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.1}>
            <div className="relative h-full rounded-2xl border border-slate-200 bg-white p-8">
              <span className="absolute right-6 top-6 text-5xl font-bold text-slate-100">{i + 1}</span>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white">
                <s.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* WHY WE BEAT TRADITIONAL OCR — Agentic VLM */}
    <section className="relative overflow-hidden bg-slate-950 py-24 text-slate-100">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(4, 135, 216, 0.35) 0, transparent 45%), radial-gradient(circle at 85% 80%, rgba(4, 135, 216, 0.25) 0, transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/30 bg-brand-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-200">
              <Sparkles className="h-3.5 w-3.5" /> Agentic Document Extraction
            </span>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Why Hewto.ai outperforms traditional EOB OCR
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-300">
              We don&apos;t use character-by-character OCR. Hewto.ai runs an agentic EOB automation
              pipeline built on <span className="text-white">vision-language models
              fine-tuned specifically for EOBs and remittance advices</span>. The model reads the
              document the way a trained payment poster does — so it captures the adjustments and
              reason codes a traditional OCR engine silently misses.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Eye className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Vision-first, not character-first</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                A VLM sees the EOB as a whole — dense payment tables, adjustment columns, reason
                code legends, page breaks, and stamps — instead of stitching guesses letter by
                letter. It tells watermark text apart from value text, label text from value text,
                and background from foreground, just like a human reader.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Brain className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Fine-tuned on remittance documents</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Our models are fine-tuned on real EOBs across payers and layouts. They know a CAS
                adjustment row is a payment reduction with a reason — not just characters in a
                column — so extractions map cleanly to 835 segments, not raw text.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Workflow className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Agentic reasoning, per field</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Each payment and adjustment runs through a purpose-built agent that verifies
                context, cross-checks claim totals against service lines, and self-corrects when
                the math doesn&apos;t balance — judgment a deterministic OCR pipeline can&apos;t make.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-2 md:grid-cols-2 md:gap-0">
            <div className="p-8">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-red-300/80">
                <X className="h-4 w-4" /> Traditional OCR misses
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                {[
                  "Adjustment columns misaligned with the wrong service line",
                  "Reason codes separated from the adjustments they explain",
                  "Multi-page EOBs split into orphaned claims",
                  "Check totals that never reconcile to claim payments",
                  "Watermarks and background print bleeding into amounts",
                  "Label text confused with the value text it describes",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-brand-500/15 to-brand-500/5 p-8 md:m-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-200">
                <CheckCircle2 className="h-4 w-4" /> Hewto.ai captures
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-100">
                {[
                  "Every adjustment aligned to its claim and service line",
                  "CARC / RARC reason codes attached to the right CAS segment",
                  "Multi-page remittances stitched into complete claims",
                  "Payments that balance — line items, claims, and check total",
                  "Watermark text separated from real value text — background noise ignored",
                  "Label text distinguished from the value text it describes",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-200" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <FineTuningCallout formName="EOB and remittance forms" />

    {/* COVERAGE + CROSSWALK */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Full remittance coverage
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Every section of the paper EOB
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Hewto.ai captures every section of the remittance document — so nothing is re-keyed
              by hand during payment posting.
            </p>
            <ul className="mt-6 space-y-3">
              {covered.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-slate-700">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-slate-200 bg-slate-950 p-6 shadow-xl">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
              <FileCode className="h-4 w-4 text-brand-200" /> remittance.835 (X12 835)
            </div>
            <pre className="mt-4 overflow-x-auto whitespace-pre text-xs leading-relaxed text-slate-200">
{`ST*835*0001*005010X221A1~
BPR*I*450.00*C*ACH*CCP~
TRN*1*CHK987654*1877260001~
NM1*PR*2*UNITEDHEALTHCARE*****PI*87726~
NM1*PE*1*SMITH*JOHN****XX*1234567893~
CLP*PATCTRL001*1*500*350*150*MB~
SVC*HC:99213*150*100**1~
CAS*CO*45*50~
CAS*PR*2*100~
PLB*1234567893*20250930*L6*0~
SE*11*0001~`}
            </pre>
            <p className="mt-4 text-xs text-slate-400">
              Generated automatically from a scanned EOB — paper remittance to 835 ERA.
            </p>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Paper EOB → X12 835 loop / segment mapping
          </p>
          <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {crosswalk.map((c) => (
              <li key={c} className="flex items-start gap-3 text-sm">
                <ArrowRightLeft className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <span className="text-slate-700">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </section>

    {/* BENEFITS */}
    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          <div className="mb-10 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
              <ShieldCheck className="h-6 w-6" />
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Why teams switch to Hewto.ai
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {benefits.map((b, i) => (
            <Reveal key={b} delay={(i % 2) * 0.06}>
              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                <span className="text-slate-700">{b}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* CAPABILITIES */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            EOB capture & 835 generation
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            EOB processing built for automated payment posting
          </h2>
        </div>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {capabilities.map((c, i) => (
          <Reveal key={c.title} delay={(i % 4) * 0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                <c.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* FAQ */}
    <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
      <Reveal>
        <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
          Paper to 835 FAQ
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          EOB to 835 conversion, answered
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          Common questions about Hewto.ai&apos;s paper to 835 conversion service for EOBs,
          EOPs, and paper remittance advices.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-8" data-testid="paper835-faq">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger
                data-testid={`paper835-faq-trigger-${i}`}
                className="text-left text-base font-semibold text-slate-900 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    <CTABand />
  </PageTransition>
);

export default PaperTo835;
